import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Plus, Search, Pin, PinOff, Trash2, Sparkles, MessageSquare,
  ArrowRight, Send, Loader2, Clock, Edit2, Save, BookMarked,
  Filter, X, Tag as TagIcon, FileText,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { HUB_DEFINITIONS, HUB_PROMPTS } from '../lib/hubPrompts';
import SaveOutputDialog from './SaveOutputDialog';
import AIModeSelector from './AIModeSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colorMap = {
  amber: { bg: 'bg-amber-50 dark:bg-amber-950/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/60 dark:border-amber-800/40', iconBg: 'bg-gradient-to-br from-amber-500 to-orange-500' },
  rose: { bg: 'bg-rose-50 dark:bg-rose-950/20', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-200/60 dark:border-rose-800/40', iconBg: 'bg-gradient-to-br from-rose-500 to-pink-500' },
  violet: { bg: 'bg-violet-50 dark:bg-violet-950/20', text: 'text-violet-600 dark:text-violet-400', border: 'border-violet-200/60 dark:border-violet-800/40', iconBg: 'bg-gradient-to-br from-violet-500 to-fuchsia-500' },
  sky: { bg: 'bg-sky-50 dark:bg-sky-950/20', text: 'text-sky-600 dark:text-sky-400', border: 'border-sky-200/60 dark:border-sky-800/40', iconBg: 'bg-gradient-to-br from-sky-500 to-cyan-500' },
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/20', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/60 dark:border-emerald-800/40', iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-500' },
  green: { bg: 'bg-green-50 dark:bg-green-950/20', text: 'text-green-600 dark:text-green-400', border: 'border-green-200/60 dark:border-green-800/40', iconBg: 'bg-gradient-to-br from-green-500 to-emerald-500' },
  blue: { bg: 'bg-blue-50 dark:bg-blue-950/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200/60 dark:border-blue-800/40', iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-500' },
  yellow: { bg: 'bg-yellow-50 dark:bg-yellow-950/20', text: 'text-yellow-600 dark:text-yellow-400', border: 'border-yellow-200/60 dark:border-yellow-800/40', iconBg: 'bg-gradient-to-br from-yellow-500 to-amber-500' },
};

function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function HubPage({ hubKey }) {
  const hub = HUB_DEFINITIONS[hubKey];
  const prompts = HUB_PROMPTS[hubKey] || [];
  const colors = colorMap[hub?.color] || colorMap.blue;
  const Icon = LucideIcons[hub?.icon] || LucideIcons.Sparkles;

  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [threads, setThreads] = useState([]);
  const [docs, setDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('all');
  const [filterPinned, setFilterPinned] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [renameOpen, setRenameOpen] = useState(null);
  const [renameText, setRenameText] = useState('');
  const [tplApply, setTplApply] = useState(null); // template to apply
  const [tplValues, setTplValues] = useState({});
  const [defaultMode, setDefaultMode] = useState('balanced');

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [tRes, dRes, tplRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/hubs/${hubKey}/threads`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/ai-documents?hub=${hubKey}`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/ai-templates?hub=${hubKey}`),
      ]);
      if (tRes.ok) setThreads(await tRes.json());
      if (dRes.ok) setDocs(await dRes.json());
      if (tplRes.ok) setTemplates(await tplRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId, hubKey]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Alias old name for backwards-compat references in this file
  const fetchThreads = fetchAll;

  // Auto-open thread if navigated with state from AI Command Center
  useEffect(() => {
    const targetThreadId = location.state?.openThreadId;
    if (targetThreadId) {
      openThread(targetThreadId);
      // Clear state so refresh doesn't reopen
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openThreadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewChat = async (initialMessage = null, starterPromptId = null) => {
    if (!wsId) return;
    // Open dialog IMMEDIATELY with a temporary loading thread so the user
    // sees feedback right away (creating the thread + first AI reply can take 5-40s).
    const nowIso = new Date().toISOString();
    if (initialMessage) {
      setActiveThread({
        thread_id: '__pending__',
        title: 'New Conversation',
        hub: hubKey,
        messages: [{ role: 'user', content: initialMessage, timestamp: nowIso }],
        message_count: 1,
        __pending: true,
      });
      setChatOpen(true);
    }
    try {
      // Step 1: Create thread instantly (no AI call) — returns in <1s
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/hubs/${hubKey}/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initial_message: initialMessage,
          starter_prompt_id: starterPromptId,
          mode: defaultMode,
        }),
      });
      if (!res.ok) {
        toast.error('Failed to start chat');
        setChatOpen(false);
        setActiveThread(null);
        return;
      }
      const t = await res.json();
      // Step 2: If we have an initial message, trigger AI generation separately.
      // The thread already has the user message. Now wait for AI.
      if (initialMessage) {
        // Keep __pending true so "Thinking..." shows
        setActiveThread({ ...t, __pending: true });
        if (!chatOpen) setChatOpen(true);
        try {
          const genRes = await apiFetch(`${API_URL}/api/threads/${t.thread_id}/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          });
          if (genRes.ok) {
            const gen = await genRes.json();
            // Re-fetch full thread to get the assistant message in the messages array
            const full = await apiFetch(`${API_URL}/api/threads/${t.thread_id}`);
            if (full.ok) {
              setActiveThread(await full.json());
            } else {
              setActiveThread(prev => ({
                ...prev,
                __pending: false,
                messages: [...(prev?.messages || []), gen.assistant_message],
                message_count: (prev?.message_count || 0) + 1,
              }));
            }
          } else {
            setActiveThread(prev => ({ ...prev, __pending: false }));
            toast.error('AI failed to respond. You can send another message to retry.');
          }
        } catch {
          setActiveThread(prev => ({ ...prev, __pending: false }));
          toast.error('AI failed to respond');
        }
      } else {
        // No initial message — just open empty chat
        setActiveThread(t);
        if (!chatOpen) setChatOpen(true);
      }
      fetchThreads();
    } catch (e) {
      toast.error('Failed to start chat');
      setChatOpen(false);
      setActiveThread(null);
    }
  };

  const openThread = async (threadId) => {
    try {
      const res = await apiFetch(`${API_URL}/api/threads/${threadId}`);
      if (res.ok) {
        const t = await res.json();
        setActiveThread(t);
        setChatOpen(true);
      }
    } catch (e) { toast.error('Failed to open chat'); }
  };

  const togglePin = async (e, thread) => {
    e.stopPropagation();
    await apiFetch(`${API_URL}/api/threads/${thread.thread_id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned: !thread.pinned }),
    });
    fetchThreads();
  };

  const deleteThread = async (e, threadId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    await apiFetch(`${API_URL}/api/threads/${threadId}`, { method: 'DELETE' });
    fetchThreads();
    toast.success('Conversation deleted');
  };

  const renameThread = async () => {
    if (!renameOpen || !renameText.trim()) return;
    await apiFetch(`${API_URL}/api/threads/${renameOpen.thread_id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: renameText.trim() }),
    });
    setRenameOpen(null);
    setRenameText('');
    fetchThreads();
    toast.success('Renamed');
  };

  const applyTemplate = async () => {
    if (!tplApply) return;
    const res = await apiFetch(`${API_URL}/api/ai-templates/${tplApply.template_id}/render`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: tplValues }),
    });
    if (res.ok) {
      const d = await res.json();
      setTplApply(null);
      setTplValues({});
      // Start new chat seeded with the rendered template content
      startNewChat(d.rendered_content);
    }
  };

  const filteredThreads = threads.filter(t =>
    (!search || t.title?.toLowerCase().includes(search.toLowerCase())) &&
    (!filterPinned || t.pinned)
  );
  const filteredDocs = docs.filter(d =>
    (!search || (d.title || '').toLowerCase().includes(search.toLowerCase()) || (d.content || '').toLowerCase().includes(search.toLowerCase())) &&
    (filterTag === 'all' || (d.tags || []).includes(filterTag))
  );
  const allTags = [...new Set(docs.flatMap(d => d.tags || []))];
  const pinned = filteredThreads.filter(t => t.pinned);
  const recent = filteredThreads.filter(t => !t.pinned);

  if (!hub) return <div className="p-6">Unknown hub</div>;

  // When a chat is active, render ChatPanel inline (replaces hub overview)
  if (chatOpen && activeThread) {
    return (
      <div className="space-y-3" data-testid={`hub-page-${hubKey}`}>
        <ChatPanel
          thread={activeThread}
          setThread={setActiveThread}
          hub={hub}
          colors={colors}
          onBack={() => {
            setChatOpen(false);
            setActiveThread(null);
            fetchThreads();
          }}
        />
        {/* Rename Dialog still needed when accessed from within chat */}
        <Dialog open={!!renameOpen} onOpenChange={(v) => !v && setRenameOpen(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Rename conversation</DialogTitle></DialogHeader>
            <Input value={renameText} onChange={e => setRenameText(e.target.value)} placeholder="New title" />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRenameOpen(null)}>Cancel</Button>
              <Button onClick={renameThread} disabled={!renameText.trim()}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`hub-page-${hubKey}`}>
      {/* Hero Header */}
      <div className={cn("rounded-2xl border p-6", colors.bg, colors.border)}>
        <div className="flex items-start gap-4">
          <div className={cn("w-14 h-14 rounded-xl flex items-center justify-center text-white shadow-lg", colors.iconBg)}>
            <Icon className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">{hub.name}</h1>
            <p className={cn("text-sm font-medium mt-0.5", colors.text)}>{hub.tagline}</p>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{hub.description}</p>
            {hub.disclaimer && (
              <p className="text-[11px] text-muted-foreground italic mt-2 max-w-2xl">⚠ {hub.disclaimer}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <AIModeSelector value={defaultMode} onChange={setDefaultMode} size="md" />
            <Button
              size="lg"
              className={cn("text-white shadow", colors.iconBg)}
              onClick={() => startNewChat()}
              data-testid="new-hub-chat-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New Chat
            </Button>
          </div>
        </div>
      </div>

      {/* Starter Prompts */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className={cn("w-4 h-4", colors.text)} />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Suggested Prompts</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {prompts.map(p => (
            <button
              key={p.id}
              onClick={() => startNewChat(p.prompt, p.id)}
              className={cn(
                "group text-left rounded-xl border p-4 transition-all hover:shadow-md hover:-translate-y-0.5 bg-card",
                colors.border
              )}
              data-testid={`prompt-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <h3 className="text-sm font-semibold leading-tight">{p.title}</h3>
                <ArrowRight className={cn("w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity", colors.text)} />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{p.hint}</p>
            </button>
          ))}
        </div>
      </div>

      {/* My Templates */}
      {templates.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BookMarked className={cn("w-4 h-4", colors.text)} />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">My Templates</h2>
            <Badge variant="secondary" className="text-[10px]">{templates.length}</Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map(t => (
              <button
                key={t.template_id}
                onClick={() => {
                  setTplApply(t);
                  const initial = {};
                  (t.placeholders || []).forEach(p => { initial[p.key] = p.default || ''; });
                  setTplValues(initial);
                }}
                className={cn("text-left rounded-xl border p-3 bg-card hover:shadow-md hover:-translate-y-0.5 transition-all", colors.border)}
                data-testid={`template-${t.template_id}`}
              >
                <div className="flex items-start justify-between">
                  <BookMarked className={cn("w-4 h-4", colors.text)} />
                  {t.usage_count > 0 && <Badge variant="secondary" className="text-[9px]">Used {t.usage_count}×</Badge>}
                </div>
                <h3 className="text-sm font-semibold mt-1.5 line-clamp-1">{t.title}</h3>
                {t.description && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{t.description}</p>}
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(t.placeholders || []).slice(0, 4).map(p => (
                    <Badge key={p.key} variant="outline" className="text-[9px]">{p.label || p.key}</Badge>
                  ))}
                  {(t.placeholders || []).length > 4 && <Badge variant="outline" className="text-[9px]">+{t.placeholders.length - 4} more</Badge>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Saved Documents */}
      {docs.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FileText className={cn("w-4 h-4", colors.text)} />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Saved Documents</h2>
            <Badge variant="secondary" className="text-[10px]">{docs.length}</Badge>
            {allTags.length > 0 && (
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger className="w-32 h-7 text-xs ml-2" data-testid="hub-doc-tag-filter">
                  <SelectValue placeholder="All Tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredDocs.slice(0, 6).map(d => (
              <div key={d.document_id} className="rounded-lg border bg-card p-3 hover:shadow-sm transition-shadow">
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold line-clamp-1">{d.title}</p>
                    <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{(d.content || '').slice(0, 90)}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(d.attached_contacts || []).slice(0, 2).map(c => (
                        <Badge key={c.contact_id} className="text-[9px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30">{c.name}</Badge>
                      ))}
                      {(d.tags || []).slice(0, 2).map(t => <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {filteredDocs.length > 6 && (
            <Link to="/documents" className={cn("text-xs mt-2 inline-flex items-center gap-1", colors.text)}>
              View all {filteredDocs.length} <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <Card className="bg-muted/30">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search conversations & documents..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-8 text-sm"
                data-testid="search-hub"
              />
            </div>
            <button
              onClick={() => setFilterPinned(!filterPinned)}
              className={cn(
                "text-xs px-2.5 py-1 rounded-md border transition-colors flex items-center gap-1",
                filterPinned ? "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/30" : "bg-card border-border hover:bg-muted/50"
              )}
              data-testid="filter-pinned"
            >
              <Pin className="w-3 h-3" /> Pinned only
            </button>
            {(search || filterPinned || filterTag !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setFilterPinned(false); setFilterTag('all'); }}>
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Saved Threads */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageSquare className={cn("w-4 h-4", colors.text)} />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Saved Conversations</h2>
            <Badge variant="secondary" className="text-[10px]">{threads.length}</Badge>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}</div>
        ) : threads.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">No conversations yet</p>
              <p className="text-xs text-muted-foreground mt-1">Click a suggested prompt above or start a new chat</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {pinned.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                  <Pin className="w-3 h-3" /> Pinned
                </p>
                <div className="space-y-2">
                  {pinned.map(t => (
                    <ThreadRow key={t.thread_id} thread={t} onClick={() => openThread(t.thread_id)}
                      onPin={togglePin} onDelete={deleteThread}
                      onRename={() => { setRenameOpen(t); setRenameText(t.title); }} />
                  ))}
                </div>
              </div>
            )}
            {recent.length > 0 && (
              <div>
                {pinned.length > 0 && (
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Recent
                  </p>
                )}
                <div className="space-y-2">
                  {recent.map(t => (
                    <ThreadRow key={t.thread_id} thread={t} onClick={() => openThread(t.thread_id)}
                      onPin={togglePin} onDelete={deleteThread}
                      onRename={() => { setRenameOpen(t); setRenameText(t.title); }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat now opens inline (see top of return - chatOpen branch) — no floating dialog */}

      {/* Rename Dialog */}
      <Dialog open={!!renameOpen} onOpenChange={(v) => !v && setRenameOpen(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Rename conversation</DialogTitle></DialogHeader>
          <Input value={renameText} onChange={e => setRenameText(e.target.value)} placeholder="New title" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(null)}>Cancel</Button>
            <Button onClick={renameThread} disabled={!renameText.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Apply Dialog */}
      <Dialog open={!!tplApply} onOpenChange={(v) => !v && setTplApply(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Use Template: {tplApply?.title}</DialogTitle>
          </DialogHeader>
          {tplApply?.description && <p className="text-xs text-muted-foreground">{tplApply.description}</p>}
          {(tplApply?.placeholders || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">This template has no placeholders. Click Apply to start a chat with it.</p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {(tplApply?.placeholders || []).map(p => (
                <div key={p.key} className="space-y-1">
                  <Label className="text-xs">{p.label || p.key}</Label>
                  <Input
                    value={tplValues[p.key] || ''}
                    onChange={e => setTplValues(prev => ({ ...prev, [p.key]: e.target.value }))}
                    placeholder={`Enter ${p.label || p.key}...`}
                    data-testid={`tpl-input-${p.key}`}
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTplApply(null)}>Cancel</Button>
            <Button onClick={applyTemplate} data-testid="apply-template-btn">Apply & Start Chat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ThreadRow({ thread, onClick, onPin, onDelete, onRename }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg border bg-card hover:bg-muted/40 p-3 transition-colors group flex items-center gap-3"
      data-testid={`thread-${thread.thread_id}`}
    >
      <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{thread.title || 'Untitled'}</p>
        <p className="text-[11px] text-muted-foreground">{thread.message_count || 0} messages · {formatTimestamp(thread.updated_at)}</p>
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button asChild variant="ghost" size="icon" className="h-7 w-7">
          <span onClick={(e) => { e.stopPropagation(); onRename(); }}><Edit2 className="w-3.5 h-3.5" /></span>
        </Button>
        <Button asChild variant="ghost" size="icon" className="h-7 w-7">
          <span onClick={(e) => onPin(e, thread)}>
            {thread.pinned ? <PinOff className="w-3.5 h-3.5 text-amber-500" /> : <Pin className="w-3.5 h-3.5" />}
          </span>
        </Button>
        <Button asChild variant="ghost" size="icon" className="h-7 w-7 text-destructive">
          <span onClick={(e) => onDelete(e, thread.thread_id)}><Trash2 className="w-3.5 h-3.5" /></span>
        </Button>
      </div>
    </button>
  );
}

function ChatPanel({ thread, setThread, hub, colors, onBack }) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [saveDialog, setSaveDialog] = useState(null); // { content, mode } when open
  const [chatMode, setChatMode] = useState(thread?.mode || 'balanced');
  const scrollRef = useRef(null);
  const Icon = LucideIcons[hub?.icon] || LucideIcons.Sparkles;

  // Sync chatMode when thread changes (e.g., user opens different saved thread)
  useEffect(() => {
    setChatMode(thread?.mode || 'balanced');
  }, [thread?.thread_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist mode change to backend
  const handleModeChange = async (newMode) => {
    setChatMode(newMode);
    if (!thread || thread.thread_id === '__pending__') return;
    try {
      await apiFetch(`${API_URL}/api/threads/${thread.thread_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: newMode }),
      });
      setThread(prev => ({ ...prev, mode: newMode }));
    } catch {}
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread?.messages?.length]);

  const sendMessage = async () => {
    if (!input.trim() || !thread) return;
    setSending(true);
    const userMsg = { role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    setThread(prev => ({ ...prev, messages: [...(prev?.messages || []), userMsg] }));
    const messageContent = input.trim();
    setInput('');
    try {
      const res = await apiFetch(`${API_URL}/api/threads/${thread.thread_id}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: messageContent, mode: chatMode }),
      });
      if (res.ok) {
        const data = await res.json();
        setThread(prev => ({
          ...prev,
          title: data.thread_title || prev.title,
          messages: [...(prev?.messages || []).filter(m => m !== userMsg), data.user_message, data.assistant_message],
        }));
      } else {
        toast.error('Failed to send');
      }
    } catch (e) {
      toast.error('Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (!thread) return null;

  return (
    <div className="rounded-2xl border bg-card overflow-hidden flex flex-col" style={{ minHeight: 'calc(100vh - 12rem)' }} data-testid="hub-chat-panel">
      {/* Chat header with back button */}
      <div className="px-5 py-3 border-b flex items-center gap-3 flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-to-hub-btn" className="h-8 w-8">
          <LucideIcons.ArrowLeft className="w-4 h-4" />
        </Button>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center text-white", colors.iconBg)}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold truncate">{thread.title}</p>
          <p className="text-[11px] text-muted-foreground">{hub.name} · {thread.message_count || 0} messages</p>
        </div>
        <AIModeSelector value={chatMode} onChange={handleModeChange} size="sm" />
      </div>

      <ScrollArea className="flex-1 px-5 py-4" ref={scrollRef}>
        <div className="space-y-4">
          {(thread.messages || []).length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Start the conversation below
            </div>
          ) : (
            (thread.messages || []).map((msg, idx) => (
              <div key={idx} className={cn("flex gap-3", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                {msg.role === 'assistant' && (
                  <div className={cn("w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-white", colors.iconBg)}>
                    <Sparkles className="w-3.5 h-3.5" />
                  </div>
                )}
                <div className="flex flex-col gap-1.5 max-w-[80%]">
                  <div className={cn(
                    "rounded-2xl px-4 py-2.5 whitespace-pre-wrap text-sm leading-relaxed",
                    msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  )}>
                    {msg.content}
                  </div>
                  {msg.role === 'assistant' && msg.content && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSaveDialog({ content: msg.content, mode: 'document' })}
                        className="w-7 h-7 rounded-md border bg-card hover:bg-muted/60 inline-flex items-center justify-center transition-colors"
                        title="Save as Document"
                        data-testid={`save-msg-doc-${idx}`}
                      >
                        <Save className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => setSaveDialog({ content: msg.content, mode: 'template' })}
                        className="w-7 h-7 rounded-md border bg-card hover:bg-muted/60 inline-flex items-center justify-center transition-colors"
                        title="Save as Template"
                        data-testid={`save-msg-tpl-${idx}`}
                      >
                        <BookMarked className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => { navigator.clipboard.writeText(msg.content); toast.success('Copied'); }}
                        className="w-7 h-7 rounded-md border bg-card hover:bg-muted/60 inline-flex items-center justify-center transition-colors"
                        title="Copy"
                      >
                        <LucideIcons.Copy className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {(sending || thread.__pending) && (
            <div className="flex gap-3 justify-start">
              <div className={cn("w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-white", colors.iconBg)}>
                <Sparkles className="w-3.5 h-3.5" />
              </div>
              <div className="rounded-2xl px-4 py-2.5 bg-muted text-sm flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking...
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="px-5 py-3 border-t flex-shrink-0">
        <div className="flex gap-2 items-end">
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={`Message ${hub.name}...`}
            className="resize-none min-h-[44px] max-h-32"
            rows={1}
            disabled={sending || thread.__pending}
            data-testid="chat-input"
          />
          <Button onClick={sendMessage} disabled={!input.trim() || sending || thread.__pending} data-testid="send-msg-btn">
            {(sending || thread.__pending) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1.5">Enter to send · Shift+Enter for new line</p>
      </div>

      {/* Save Output Dialog (still uses dialog modal for the save form itself) */}
      <SaveOutputDialog
        open={!!saveDialog}
        onOpenChange={(v) => !v && setSaveDialog(null)}
        content={saveDialog?.content}
        hub={thread.hub}
        sourceThreadId={thread.thread_id}
        defaultMode={saveDialog?.mode || 'document'}
      />
    </div>
  );
}
